<template>
  <div class="production-priority-page">
    <div class="priority-header">
      <div class="d-flex align-items-center gap-3">
        <h2 class="m-0">{{ $t("productionPriority.title") }}</h2>
        <div class="btn-group btn-group-sm">
          <button
            class="btn"
            :class="view === 'board' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="switchView('board')"
          >
            {{ $t("productionPriority.viewBoard") }}
          </button>
          <button
            class="btn"
            :class="
              view === 'timeline' ? 'btn-primary' : 'btn-outline-secondary'
            "
            @click="switchView('timeline')"
          >
            {{ $t("productionPriority.viewTimeline") }}
          </button>
        </div>
      </div>
      <button class="btn btn-sm btn-outline-primary" @click="loadData">
        {{ $t("productionPriority.refresh") }}
      </button>
    </div>

    <div v-if="loading" class="m-5 p-5 text-center bold-40">
      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
      {{ $t("productionPriority.loading") }}
    </div>

    <div v-else-if="errorMessage" class="nature-white-box text-center py-4">
      <div class="alert alert-danger">{{ errorMessage }}</div>
      <button class="btn btn-sm btn-outline-danger" @click="loadData">
        {{ $t("productionPriority.retry") }}
      </button>
    </div>

    <div v-else-if="view === 'board'" class="board-scroll">
      <div class="board">
        <div
          v-for="column in columns"
          :key="column.column_key"
          class="board-column"
          :class="'board-column--' + column.type"
        >
          <div class="column-header">
            <div class="column-title">
              <span class="column-name" :title="column.name">{{
                column.name
              }}</span>
              <span class="column-title-actions">
                <button
                  v-if="column.type === 'partner'"
                  class="btn btn-sm btn-outline-secondary capacity-edit-btn"
                  :title="$t('productionPriority.editCapacity')"
                  @click="openCapacity(column)"
                >
                  <BaseIcon name="settings" />
                </button>
                <span
                  class="badge"
                  :class="
                    column.type === 'partner' ? 'bg-info' : 'bg-secondary'
                  "
                >
                  {{ columnTypeLabel(column.type) }}
                </span>
              </span>
            </div>
            <div class="column-capacity">
              <template v-if="column.type === 'partner'">
                <span v-if="column.capacity_hours">
                  {{
                    $t("productionPriority.capacityOf", {
                      used: capacityHours(column),
                      total: column.capacity_hours,
                    })
                  }}
                </span>
                <span v-else>{{ $t("productionPriority.noCapacity") }}</span>
              </template>
              <template v-else-if="column.type === 'warehouse'">
                <span>
                  {{
                    $t("productionPriority.scheduledHours", {
                      hours: column.used_hours,
                    })
                  }}
                </span>
              </template>
              <span class="text-muted">
                &middot;
                {{
                  $t("productionPriority.queuedHours", {
                    hours: queuedHours(column),
                  })
                }}
              </span>
              <span class="text-muted">&middot; {{ column.cards.length }}</span>
            </div>
            <div
              v-if="column.type === 'partner' && column.capacity_hours"
              class="capacity-bar"
            >
              <div
                class="capacity-bar-fill"
                :class="{
                  'bg-danger': capacityRatio(column) > 1,
                  'bg-warning':
                    capacityRatio(column) > 0.7 && capacityRatio(column) <= 1,
                }"
                :style="{
                  width: Math.min(100, capacityRatio(column) * 100) + '%',
                }"
              ></div>
            </div>
          </div>

          <draggable
            v-model="column.cards"
            group="production-priority"
            item-key="id"
            class="column-body"
            :animation="150"
            @change="onColumnChange(column, $event)"
          >
            <template #item="{ element: card }">
              <div
                class="priority-card"
                :class="{ 'is-overdue': isOverdue(card) }"
              >
                <div class="card-top">
                  <span class="card-priority">#{{ card.priority_label }}</span>
                  <span class="card-id">{{ card.pretty_id }}</span>
                </div>
                <div
                  v-for="item in card.items"
                  :key="item.id"
                  class="card-item"
                >
                  <img
                    v-if="item.product.image"
                    :src="productImageUrl(item.product.image)"
                    class="card-image"
                    alt=""
                  />
                  <BaseIcon v-else name="no-image" class="card-image" />
                  <div class="card-item-info">
                    <div class="card-item-name" :title="item.product.name">
                      {{ item.product.name }}
                    </div>
                    <div class="card-item-meta">
                      <span>
                        {{ $t("productionPriority.qty") }}:
                        {{ item.quantity }}
                      </span>
                      <span v-if="item.sku"> &middot; {{ item.sku }} </span>
                    </div>
                  </div>
                </div>
                <div class="card-footer">
                  <span v-if="card.deadline_date" class="card-deadline">
                    {{ $t("productionPriority.deadline") }}:
                    {{ formatDate(card.deadline_date) }}
                  </span>
                  <span class="card-hours"> {{ card.estimated_hours }}h </span>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>
    </div>

    <ProductionTimeline
      v-else-if="view === 'timeline'"
      :days="timelineDays"
      :boards="timelineBoards"
      @reload="reloadTimeline"
    />

    <Modal
      ref="capacityModal"
      :title="$t('productionPriority.capacityTitle')"
      className="modal-lg"
    >
      <div v-if="capacityColumn">
        <div v-if="capacityLoading" class="text-center py-4">
          <div
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></div>
          {{ $t("productionPriority.loading") }}
        </div>
        <div v-else>
          <div class="base-capacity-section">
            <div class="base-capacity-title">
              <h6 class="mb-1">
                {{ $t("productionPriority.baseCapacityTitle") }}
              </h6>
              <span class="text-muted small">
                {{ $t("productionPriority.baseCapacityHint") }}
              </span>
            </div>
            <div class="row g-2">
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.minimumHours")
                }}</label>
                <input
                  v-model.number="capacityBase.minimum_hours"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.expectedHours")
                }}</label>
                <input
                  v-model.number="capacityBase.expected_hours"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.maximumHours")
                }}</label>
                <input
                  v-model.number="capacityBase.maximum_hours"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.confidence")
                }}</label>
                <input
                  v-model.number="capacityBase.confidence"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  class="form-control form-control-sm"
                />
              </div>
            </div>
          </div>
          <div class="capacity-weeks-title">
            <h6 class="mb-2">
              {{ $t("productionPriority.weeklyCapacityTitle") }}
            </h6>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ $t("productionPriority.week") }}</th>
                  <th>{{ $t("productionPriority.minimumHours") }}</th>
                  <th>{{ $t("productionPriority.expectedHours") }}</th>
                  <th>{{ $t("productionPriority.maximumHours") }}</th>
                  <th>{{ $t("productionPriority.confidence") }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="week in capacityWeeks"
                  :key="week.week_start"
                  :class="{ 'week-is-base': !week.custom }"
                >
                  <td class="text-nowrap">
                    {{ formatDate(week.week_start) }}
                    <span
                      v-if="!week.custom"
                      class="badge bg-light text-muted ms-1"
                      >{{ $t("productionPriority.fromBase") }}</span
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="week.minimum_hours"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="week.expected_hours"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="week.maximum_hours"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="week.confidence"
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td class="text-center">
                    <button
                      v-if="week.forecast_id"
                      class="btn btn-sm btn-outline-danger"
                      :title="$t('productionPriority.delete')"
                      :disabled="capacitySaving"
                      @click="deleteCapacityWeek(week)"
                    >
                      <BaseIcon name="trash" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="capacitySaving"
            @click="closeCapacity"
          >
            {{ $t("productionPriority.cancel") }}
          </button>
          <button
            class="btn btn-sm btn-primary"
            :disabled="capacitySaving"
            @click="saveCapacity"
          >
            <span
              v-if="capacitySaving"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            {{ $t("productionPriority.save") }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import dayjs from "dayjs";
import draggable from "vuedraggable";
import BaseIcon from "@/components/icons/BaseIcon";
import Modal from "@/components/modals/Modal.vue";
import ProductionTimeline from "./ProductionTimeline.vue";
import http from "@/modules/http";
import tenant from "@/modules/tenant";

export default {
  name: "ProductionPriority",
  components: { draggable, BaseIcon, Modal, ProductionTimeline },
  data() {
    return {
      loading: true,
      errorMessage: "",
      view: "board",
      columns: [],
      timelineDays: [],
      timelineBoards: [],
      capacityColumn: null,
      capacityWeeks: [],
      capacityBase: {
        minimum_hours: "",
        expected_hours: "",
        maximum_hours: "",
        confidence: "",
      },
      capacityLoading: false,
      capacitySaving: false,
    };
  },
  computed: {
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
  },
  watch: {
    capacityBase: {
      deep: true,
      handler() {
        if (!this.capacityColumn) return;
        this.capacityWeeks.forEach((week) => {
          if (week.custom) return;
          week.minimum_hours = this.capacityBase.minimum_hours ?? "";
          week.expected_hours = this.capacityBase.expected_hours ?? "";
          week.maximum_hours = this.capacityBase.maximum_hours ?? "";
          week.confidence = this.capacityBase.confidence ?? "";
        });
      },
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData(options = {}) {
      const silent = !!(options && options.silent);
      if (!silent) this.loading = true;
      this.errorMessage = "";
      const url =
        this.view === "timeline"
          ? "/production/priority/timeline"
          : "/production/priority/board";
      http
        .fetch(url)
        .then((data) => {
          if (!data) return;
          if (this.view === "timeline") {
            this.timelineDays = data.days || [];
            this.timelineBoards = data.boards || [];
          } else {
            this.buildBoard(data.columns || [], data.cards || []);
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.errorMessage = this.$t("productionPriority.loadError");
        });
    },
    reloadTimeline() {
      this.loadData({ silent: true });
    },
    switchView(view) {
      if (this.view === view) return;
      this.view = view;
      this.loadData();
    },
    buildBoard(columns, cards) {
      const columnMap = {};
      columns.forEach((column) => {
        columnMap[column.column_key] = {
          ...column,
          cards: [],
        };
      });

      const cardMap = {};
      cards.forEach((card) => {
        cardMap[card.id] = card;
      });

      cards.forEach((card) => {
        const column = columnMap[card.column_key] || columnMap["unassigned:0"];
        if (column) {
          column.cards.push(card);
        }
      });

      Object.values(columnMap).forEach((column) => {
        column.cards.sort(
          (a, b) =>
            (a.production_priority === null
              ? Number.MAX_SAFE_INTEGER
              : a.production_priority) -
            (b.production_priority === null
              ? Number.MAX_SAFE_INTEGER
              : b.production_priority)
        );
      });

      this.columns = Object.values(columnMap);
      this.refreshPriorityLabels();
    },
    refreshPriorityLabels() {
      let counter = 0;
      this.columns.forEach((column) => {
        column.cards.forEach((card) => {
          card.priority_label = ++counter;
        });
      });
    },
    onColumnChange(column, event) {
      if (event.added) {
        const card = event.added.element;
        const newIndex =
          event.added.newIndex !== undefined
            ? event.added.newIndex
            : column.cards.findIndex((c) => c.id === card.id);
        card.column_key = column.column_key;
        this.updateCardLocation(card, column, newIndex);
      } else if (event.moved) {
        this.saveColumnOrder(column);
      }
      this.refreshPriorityLabels();
    },
    updateCardLocation(card, column, newIndex) {
      http
        .fetch("/production/priority/move", {
          operation_id: card.id,
          location_type: column.type,
          location_id: column.id,
          new_index: newIndex,
        })
        .catch(() => {
          this.loadData();
        });
    },
    saveColumnOrder(column) {
      http
        .fetch("/production/priority/reorder", {
          location_type: column.type,
          location_id: column.id,
          ordered_ids: column.cards.map((c) => c.id),
        })
        .catch(() => {
          this.loadData();
        });
    },
    columnTypeLabel(type) {
      if (type === "warehouse") return this.$t("productionPriority.warehouse");
      if (type === "partner") return this.$t("productionPriority.partner");
      return this.$t("productionPriority.unassigned");
    },
    queuedHours(column) {
      return column.cards.reduce(
        (sum, card) => sum + (card.estimated_hours || 0),
        0
      );
    },
    capacityHours(column) {
      return column.used_hours + this.queuedHours(column);
    },
    capacityRatio(column) {
      if (!column.capacity_hours) return 0;
      return this.capacityHours(column) / column.capacity_hours;
    },
    isOverdue(card) {
      if (!card.deadline_date) return false;
      return dayjs(card.deadline_date).isBefore(dayjs(), "day");
    },
    formatDate(value) {
      return dayjs(value).format("YYYY-MM-DD");
    },
    productImageUrl(image) {
      if (!image?.path || !image?.name) return null;
      return (
        (this.mediaServer || "") +
        "/" +
        image.path +
        "/262-" +
        image.name +
        ".webp"
      );
    },
    openCapacity(column) {
      this.capacityColumn = column;
      this.capacityWeeks = [];
      this.capacityBase = {
        minimum_hours: "",
        expected_hours: "",
        maximum_hours: "",
        confidence: "",
      };
      this.capacityLoading = true;
      this.capacitySaving = false;
      const from = dayjs().startOf("week").format("YYYY-MM-DD");
      const to = dayjs(from)
        .add(8 * 7, "day")
        .format("YYYY-MM-DD");
      http
        .fetch(
          `/production/priority/capacity?partner_id=${column.id}&from=${from}&to=${to}`
        )
        .then((data) => {
          this.capacityLoading = false;
          const base = data?.base || {};
          this.capacityBase = {
            minimum_hours: base.minimum_hours ?? "",
            expected_hours: base.expected_hours ?? "",
            maximum_hours: base.maximum_hours ?? "",
            confidence: base.confidence ?? "",
          };
          this.capacityWeeks = this.buildCapacityWeeks(
            from,
            data?.capacity || [],
            base
          );
          this.$refs.capacityModal.showModal();
        })
        .catch(() => {
          this.capacityLoading = false;
        });
    },
    buildCapacityWeeks(from, forecasts, base) {
      const forecastMap = {};
      forecasts.forEach((f) => {
        forecastMap[dayjs(f.week_start).format("YYYY-MM-DD")] = f;
      });
      const baseValues = base || {};
      const weeks = [];
      for (let i = 0; i < 8; i++) {
        const key = dayjs(from)
          .add(i * 7, "day")
          .format("YYYY-MM-DD");
        const f = forecastMap[key];
        weeks.push({
          week_start: key,
          forecast_id: f?.id ?? null,
          minimum_hours: f?.minimum_hours ?? baseValues.minimum_hours ?? "",
          expected_hours: f?.expected_hours ?? baseValues.expected_hours ?? "",
          maximum_hours: f?.maximum_hours ?? baseValues.maximum_hours ?? "",
          confidence: f?.confidence ?? baseValues.confidence ?? "",
          custom: !!f,
        });
      }
      return weeks;
    },
    markWeekCustom(week) {
      week.custom = true;
    },
    saveCapacity() {
      if (!this.capacityColumn) return;
      this.capacitySaving = true;
      const requests = [];
      const base = this.capacityBase;
      const baseExpected =
        base.expected_hours === "" || base.expected_hours == null
          ? ""
          : base.expected_hours;

      if (baseExpected === "") {
        requests.push(
          http.fetch("/production/priority/capacity/base/delete", {
            partner_id: this.capacityColumn.id,
          })
        );
      } else {
        requests.push(
          http.fetch("/production/priority/capacity/base", {
            partner_id: this.capacityColumn.id,
            minimum_hours:
              base.minimum_hours === "" || base.minimum_hours == null
                ? ""
                : base.minimum_hours,
            expected_hours: baseExpected,
            maximum_hours:
              base.maximum_hours === "" || base.maximum_hours == null
                ? ""
                : base.maximum_hours,
            confidence:
              base.confidence === "" || base.confidence == null
                ? ""
                : base.confidence,
          })
        );
      }

      this.capacityWeeks
        .filter((week) => week.custom)
        .forEach((week) => {
          requests.push(
            http.fetch("/production/priority/capacity", {
              partner_id: this.capacityColumn.id,
              week_start: week.week_start,
              minimum_hours:
                week.minimum_hours === "" || week.minimum_hours == null
                  ? ""
                  : week.minimum_hours,
              expected_hours:
                week.expected_hours === "" || week.expected_hours == null
                  ? ""
                  : week.expected_hours,
              maximum_hours:
                week.maximum_hours === "" || week.maximum_hours == null
                  ? ""
                  : week.maximum_hours,
              confidence:
                week.confidence === "" || week.confidence == null
                  ? ""
                  : week.confidence,
            })
          );
        });

      Promise.all(requests)
        .then(() => {
          this.capacitySaving = false;
          this.$refs.capacityModal.hideModal();
          this.loadData();
        })
        .catch(() => {
          this.capacitySaving = false;
        });
    },
    deleteCapacityWeek(week) {
      if (!week.forecast_id) return;
      http
        .fetch("/production/priority/capacity/delete", {
          id: week.forecast_id,
        })
        .then(() => {
          week.forecast_id = null;
          week.custom = false;
          week.minimum_hours = this.capacityBase.minimum_hours ?? "";
          week.expected_hours = this.capacityBase.expected_hours ?? "";
          week.maximum_hours = this.capacityBase.maximum_hours ?? "";
          week.confidence = this.capacityBase.confidence ?? "";
        });
    },
    closeCapacity() {
      if (this.$refs.capacityModal) {
        this.$refs.capacityModal.hideModal();
      }
    },
  },
};
</script>

<style scoped>
.production-priority-page {
  padding: 16px;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}
.priority-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.board-scroll {
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  padding-bottom: 8px;
}
.board {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: max-content;
}
.board-column {
  width: 280px;
  flex-shrink: 0;
  background: #f4f5f7;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
}
.column-header {
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 8px 8px 0 0;
}
.column-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.column-title-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.capacity-edit-btn {
  padding: 0 6px;
  line-height: 1;
}
.base-capacity-section {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}
.base-capacity-title {
  margin-bottom: 10px;
}
.capacity-weeks-title {
  margin-top: 4px;
}
.week-is-base {
  background: #fbfbfb;
}
.week-is-base input {
  color: #6c757d;
}
.column-name {
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.column-capacity {
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
}
.capacity-bar {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  margin-top: 8px;
  overflow: hidden;
}
.capacity-bar-fill {
  height: 100%;
  background: #198754;
  border-radius: 3px;
  transition: width 0.2s;
}
.column-body {
  padding: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 60px;
}
.priority-card {
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  padding: 10px;
  margin-bottom: 8px;
  cursor: grab;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.priority-card.is-overdue {
  border-left: 3px solid #dc3545;
}
.priority-card:active {
  cursor: grabbing;
}
.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.card-priority {
  font-size: 11px;
  font-weight: 700;
  color: #6c757d;
}
.card-id {
  font-size: 11px;
  color: #6c757d;
}
.card-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.card-image {
  width: 34px;
  height: 34px;
  border-radius: 4px;
  object-fit: cover;
  background: #f4f5f7;
  flex-shrink: 0;
}
.card-item-info {
  min-width: 0;
}
.card-item-name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-item-meta {
  font-size: 11px;
  color: #6c757d;
}
.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid #f0f0f0;
  padding-top: 6px;
  font-size: 11px;
  color: #6c757d;
}
.card-deadline {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-hours {
  font-weight: 600;
  flex-shrink: 0;
}
</style>
